'use client';

import { useMemo, useEffect } from 'react';
import Image from 'next/image';
import BigNumber from 'bignumber.js';
import { useWriteContract } from '@ywwwtseng/wallet-kit/evm';
import { useWalletKitConnect } from '@ywwwtseng/wallet-kit';
import { Button, toast, useI18n, useQuery } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import { MOONINI_LOCK_ADDRESS } from '@/constants';
import MOONINI_LOCK_ABI from '@/assets/abi/MooniniLock.abi.json';
import { useInvestments, type Investment } from '@/hooks/useInvestments';
import { InvestmentCard } from '@/components/common/InvestmentCard';

export default function InvestorScreen() {

  const { t } = useI18n();
  const walletKitConnect = useWalletKitConnect();
  const bscAddress = walletKitConnect.accounts.bsc.address;

  // 查询 pending 的 right assignments（只使用 assignor，避免多次请求）
  const { data: pendingRightAssignments, refetch: refetchPendingRightAssignments } = useQuery('right_assignments_pending', {
    params: {
      assignor: bscAddress,
    },
    refetchOnMount: true,
    enabled: !!bscAddress,
  });

  // 获取用户投资信息
  const {
    investments,
    totalClaimableAmount,
    formattedTotalClaimableAmount,
    claimedAmount,
    formattedClaimedAmount,
    claimableAmount,
    formattedClaimableAmount,
    refetch,
  } = useInvestments(bscAddress);


  // 领取
  const { writeContract: claim, isLoading: isClaiming } = useWriteContract({
    onSuccess: () => {
      toast.success(t('investor.claim_success') || 'Claimed successfully');
      refetch();
    },
    onError: (error: Error) => {
      toast.error(error.message || t('investor.claim_failed') || 'Failed to claim');
    },
  });


  const handleClaim = async () => {
    if (!MOONINI_LOCK_ADDRESS || !bscAddress) return;
    if (!claimableAmount || BigInt(claimableAmount.toString()) === 0n) {
      toast.error(t('investor.no_claimable') || 'No claimable amount');
      return;
    }

    await claim({
      address: MOONINI_LOCK_ADDRESS,
      abi: MOONINI_LOCK_ABI,
      functionName: 'claim',
      args: [],
    });
  };


  const claimProgress = useMemo(() => {
    if (!investments || investments.length === 0) return 0;
    if (totalClaimableAmount.eq(0) || claimedAmount.eq(0)) return 0;
    return Number((new BigNumber(claimedAmount).multipliedBy(100).dividedBy(totalClaimableAmount)).toFixed(1));
  }, [totalClaimableAmount, claimedAmount]);

  useEffect(() => {
    void walletKitConnect.switchNetwork(process.env.NEXT_PUBLIC_APP_ENV === 'production' ? 'bsc' : 'bscTestnet');
  }, []);


  if (!bscAddress) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-4 py-3 px-5">
        <Button
          size="sm"
          rounded="full"
          variant="contained"
          color="primary"
          onClick={() => void walletKitConnect.open()}
        >
          {t('investor.connect_wallet')}
        </Button>
        <Typography size="2" color="rgba(255,255,255,0.54)" className="text-center">
          {t('investor.connect_wallet_hint')}
        </Typography>
      </div>
    );
  }

  if (!investments) {
    return null;
  }


  if (!MOONINI_LOCK_ADDRESS) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-4 py-3 px-5">
        <Typography size="4" weight={500}>
          {t('investor.not_available')}
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-start h-full w-full gap-4 py-3 px-5">
      {/* 投资信息卡片 - 突出显示 */}
      <div className="flex flex-col items-center justify-start h-full w-full gap-2">
        {investments.length > 0 ? (
          <>
            {/* <Image src="/images/tokens/moonini.png" alt="moonini" width={56} height={56} /> */}
            <div className="h-stack w-full justify-between px-3">
              <Typography size="2" weight={500} i18n="investor.total_claimable" />
              <div className="h-stack items-center gap-1">
                <Image src="/images/tokens/moonini.png" alt="moonini" width={20} height={20} />
                <Typography size="2" weight={500} className="text-primary">
                  {formattedTotalClaimableAmount} $MOONINI
                </Typography>
              </div>
            </div>

            {/* 进度条 */}
            <div className="v-stack w-full gap-2 px-3">
              <div className="h-stack w-full justify-between">
                <Typography size="2" weight={500} i18n="investor.claimed" />
                <Typography size="2" weight={500}>
                  {formattedClaimedAmount} $MOONINI ({claimProgress.toFixed(1)}%)
                </Typography>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden relative" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                <div
                  className="h-full transition-all duration-300 ease-out rounded-full"
                  style={{ width: `${claimProgress}%`, backgroundColor: '#3b82f6' }}
                />
              </div>
            </div>

            {investments.length > 0 && (
              <div className="v-stack w-full gap-2 mt-2">
                {investments.map((investment: Investment, index: number) => (
                  <InvestmentCard
                    key={index}
                    investment={investment}
                    index={index}
                    assignor={bscAddress}
                    pendingRightAssignments={pendingRightAssignments as Array<{
                      id: string;
                      assign_id: string;
                      investment_index: number;
                      assignor: string;
                    }> | undefined}
                    onCancelSuccess={() => {
                      void refetch();
                      void refetchPendingRightAssignments();
                    }}
                  />
                ))}
              </div>
            )}

            <div className="w-full mt-auto">
              <Button
                width='full'
                color="primary"
                variant="contained"
                size="sm"
                rounded="full"
                isLoading={isClaiming}
                onClick={handleClaim}
                disabled={!claimableAmount || BigInt(claimableAmount.toString()) === 0n}
              >
                <Typography size="3" weight={500} i18n="investor.claim" params={{ amount: formattedClaimableAmount }}>
                </Typography>
              </Button>
            </div>
          </>
        ) : (
          <div className="v-stack h-full w-full items-center gap-2 py-4">
            <Typography size="2" color="rgba(255,255,255,0.54)">
              {t('investor.investment_not_found')}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
